/**
 * @file src/Main.js
 * @description Composition root for the Orbiters app (entry point referenced by index.html).
 * Runs the boot prelude (UI-core shim, environment guards, loading-screen bridge, i18n),
 * then constructs the runtime via `createOrbitersApp` and starts it. All runtime
 * construction and domain logic live in `orbitersApp.js` and the modules it composes.
 * @version 2.0.0
 * @author 𝐵𝓇𝓊𝓃𝒶 𝒢𝓊𝒶𝓇𝓃𝒾𝑒𝓇𝒾
 * @license MIT
 * @memberof CoreModule
 */

// The audio context is built here, first, and nowhere else. `latencyHint` — the device
// callback size, and with it the audio CPU budget — is read only by the AudioContext
// constructor, so the hint has to be decided before any module in the graph so much as
// reads Tone's global context (which would silently create one with Tone's own default).
// This import is a side effect on purpose; it must stay above every other import.
import './audio/audioContextBoot.js';

// uiCoreShim publishes window.uiCore (Bootstrap-like Dropdown/Modal/Tooltip helpers) used by
// notifications/tooltips — imported first so its side-effect runs before anything reads it.
import './ui/uiCoreShim.js';

import { initI18n, getT } from './i18n/index.js';
import { installEnvGuards } from './boot/envGuards.js';
import { installLoadingScreenBridge, showBootMessage } from './boot/loadingScreen.js';
import { bootTrace } from './boot/bootTrace.js';
import { installAudioOffsetRuntimeHandle } from './config/audioOffset.js';
import { installMetronome } from './audio/metronome.js';
import { createOrbitersApp } from './orbitersApp.js';
import { resolveBootTargetFromUrl, getQueueStartFromUrl, getRoomIdFromUrl } from './utils/urlParams.js';
import { getLoginHref } from './ui/loginPrompt.js';
import { createMultiOrbiterApp } from './multi/createMultiOrbiterApp.js';
import { createCollectionApp, renderMessage } from './multi/createCollectionApp.js';
import { fetchCollectionData } from './api/collectionDataService.js';
import { resolveCollectionForBoot } from './offline/offlineCollectionBoot.js';
import { establishOfflineOwner } from './offline/offlineOwner.js';
import { bootLocalLibrary } from './local/localLibrary.js';
import { isOfflineReadOnly } from './offline/offlineMode.js';
import { registerOfflineWorker } from './offline/registerOfflineWorker.js';
import {
    collectionAssetBindings,
    createOfflineAssetBindingScope,
    replaceOfflineAssetBindingScope,
} from './offline/offlineAssetBindings.js';
import { isOfflineDownloadsEnabled } from './offline/offlineConfig.js';
import { fetchQueueData, buildQueueData } from './api/queueDataService.js';
import { makeOrbiterVoiceSession } from './multi/makeOrbiterVoiceSession.js';
import { createViewportCompositor } from './multi/renderHost.js';

// 1. Boot prelude — must run before the app constructs anything (no app state needed).
installEnvGuards();
installLoadingScreenBridge();
// Expose the per-device manual audio offset for by-ear tuning (window.orbitersAudioOffset)
// and to seed the cache from `?audioOffset=`/localStorage before the first scheduled start.
installAudioOffsetRuntimeHandle();
// Wire the device metronome singleton to its toggle + the capture-mute gate.
installMetronome();

// 2. i18n must resolve before the first translated paint (React regions read `getT()` at render);
// keep the document title in sync with the active locale.
await initI18n();
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    document.title = getT()('app.title');
    window.addEventListener('languageChanged', () => {
        document.title = getT()('app.title');
    });
}

// 3. Construct the runtime and boot it on DOM-ready (initial session → React mount → render).
// Boot dispatch (three branches; single-orbiter is the byte-identical default):
//   - `?collection=<id>`  → native collection mode (decisions/0004): fetch the collection (roster +
//     permissions + saved layout) and compose it into the ported resizable slot layout over the shared
//     realm. The host opens this full-screen; there is NO per-frame bridge.
//   - `?multi=1` + `?roster=` → the multi-orbiter realm from an inline roster.
//   - otherwise            → single-orbiter (unchanged). A stray flag with no valid data falls through.
// The branch is resolved ONCE, by `resolveBootTargetFromUrl` — the same call that suppresses edit mode
// for the multi-stage branches (edit is a single-orbiter surface: it edits one orbiter). Dispatching and
// suppressing off one resolver is what keeps a collection URL from also raising the studio.
const bootParams = new URLSearchParams(window.location.search);
const { kind: bootKind, collectionId, queueMode, queueTracks, roster } = resolveBootTargetFromUrl(bootParams);

// WHOSE browser this is, before anything reads state that belongs to a person.
//
// It used to be armed inside the collection branch alone, because the stored manifest was the only
// per-user thing a boot read. It is no longer: MIDI mappings are mirrored per owner too, and a
// single-orbiter session is exactly where somebody uses a controller. Armed here, the gate is up
// before any of those reads, on every boot.
//
// NOT awaited, and that is the point: arming is synchronous, so a read that needs the answer waits
// for it while the rest of the boot does not. Awaiting would put an auth handshake on the critical
// path to the app existing — the thing that was deliberately moved off it — and the call is
// idempotent, so the collection branch asking again costs one round trip, not two.
void establishOfflineOwner();

// Open this machine's local library NOW, alongside the collection fetch rather than after it.
//
// The library is entirely local — an IndexedDB open, a presence pass and the vault sweep — and it
// shares not one byte with the collection request, yet it used to wait for it: the rail controller
// is built from `collectionData`, so its `start()` could not run until the fetch, the arrangement
// read and the studio mount had all finished. Measured on a four-deck collection, that put the
// local-file decks ~4.4s behind the platform ones, on a wait they had no reason to be in — the
// files were on the disk the whole time.
//
// `bootLocalLibrary` is memoized, so this is not a second boot: the rail's own `await` joins THIS
// promise and gets whatever it has already done for free. Kicked only where the rail actually
// exists (a collection outside a room — see `localRail` in createCollectionApp), because the boot
// ends in a vault sweep that DELETES orphaned copies, and a sweep is not something to start
// speculatively in a session that was never going to run one.
//
// Ordering with the owner gate is unchanged: `establishOfflineOwner` above arms it synchronously,
// so this read waits behind the same gate it always did, and cannot see the previous owner's files.
if (bootKind === 'collection' && !getRoomIdFromUrl(bootParams)) {
    void bootLocalLibrary().catch(() => {
        // A library that cannot open is not a boot failure — the rail's own start() reports it, and
        // the collection opens exactly as it does on a machine with no local files at all.
    });
}

let app;
const collectionAssetBindingScope = createOfflineAssetBindingScope();
if (bootKind === 'queue') {
    // Queue mode: same multi-stage layout as collection mode, but the stages
    // come from the user's playable queue (backend) or an inline `?tracks=` list — never
    // the collection loader (a queue is not a collection). Like collection mode, a
    // failure surfaces a message; it never falls back to single-orbiter.
    try {
        showBootMessage(getT()('loading.queue', 'Loading queue...'));
        const startTrackId = getQueueStartFromUrl(bootParams);
        const queueData = queueMode
            ? await fetchQueueData({ startTrackId })
            : buildQueueData(queueTracks, { startTrackId });
        app = createCollectionApp({ collectionData: queueData, makeVoiceSession: makeOrbiterVoiceSession });
    } catch (error) {
        console.error('[queue] failed to load queue', error);
        app = renderMessage(
            error?.code === 'unauthorized'
                ? getT()('queueMode.signInRequired', 'Sign in to play your queue.')
                : error?.code === 'empty'
                    ? getT()('queueMode.empty', 'Your queue is empty.')
                    : getT()('queueMode.loadFailed', "Couldn't load your queue.")
        );
    }
} else if (bootKind === 'collection') {
    // Collection mode fetches its own data (stubbed until the backend lands). A fetch/permission
    // failure surfaces a message instead of a broken realm; it never falls back to single-orbiter
    // (the URL asked for a specific collection).
    try {
        // The collection fetch is a real network wait — say so instead of a black screen
        // (nothing ticked the boot overlay until the first voice booted).
        showBootMessage(getT()('loading.collection', 'Loading Collection...'));
        // `?collection=` still asks the live collection first when a connection is available. The
        // stored manifest wins only after that bounded read fails — see `offlineCollectionBoot.js`.
        // The fetch is passed in rather than imported there so the offline library page
        // can reuse the resolver without pulling the API client into its bundle.
        bootTrace('realm', 'collection fetch →');
        const { collectionData, source } = await resolveCollectionForBoot(collectionId, {
            fetchOnline: (id) => fetchCollectionData(id),
            // A room link is a live invitation — its card pool, join code, deck shape, score and
            // draw are minted onto the grant and only the live read returns them. Never the
            // manifest, connection or not.
            allowStored: !getRoomIdFromUrl(bootParams),
        });
        if (source === 'offline') {
            // Two separate facts, and the log used to conflate them exactly as the code once did:
            // reading stored bytes is not the same as being cut off, and an online manifest boot
            // keeps its writes.
            console.info(
                isOfflineReadOnly()
                    ? '[offline] Booting this collection from stored bytes; writes are disabled.'
                    : '[offline] Booting this collection from stored bytes; still online, writes allowed.',
            );
        }
        bootTrace('realm', 'collection fetched', { source });
        await replaceOfflineAssetBindingScope(
            collectionAssetBindingScope,
            collectionAssetBindings(collectionData),
        );
        app = createCollectionApp({ collectionData, makeVoiceSession: makeOrbiterVoiceSession });
    } catch (error) {
        // Playing requires an account, and a share link is a door into a room like any other.
        // Send them to sign in and bring them BACK here afterwards — landing on a generic page
        // loses the invitation, which is the whole thing they were handed.
        if (error?.code === 'sign-in-required' && getRoomIdFromUrl(bootParams)) {
            showBootMessage(getT()('room.signInRequired', 'Sign in to join this room...'));
            window.location.assign(getLoginHref(null, { returnTo: window.location.href }));
        } else {
            console.error('[collection] failed to load collection', error);
            app = renderMessage(error?.code === 'not-found' ? 'Collection not found.' : "Couldn't load this collection.");
        }
    }
} else if (bootKind === 'multi') {
    // The multi-orbiter realm renders all voices through ONE WebGLRenderer/canvas/loop (the
    // ViewportCompositor) so compressed textures upload for every voice — N canvases = N contexts, where
    // only the first textured. The shell owns its lifecycle (disposed with the realm).
    app = createMultiOrbiterApp({
        roster,
        makeVoiceSession: makeOrbiterVoiceSession,
        createRenderHost: createViewportCompositor,
    });
} else {
    app = createOrbitersApp();
}
// `app` is unset only when the boot handed the page over to somewhere else — today that is the
// sign-in redirect on a room link. Navigation is not instant, and starting a realm we are about to
// leave would boot audio and a renderer for the half-second before the page goes.
if (app) app.start();

// The worker installs AFTER the app has been handed its start, never before. Precaching the whole
// build is several megabytes of requests, and doing that while the first frame, the audio graph and
// the collection fetch are all competing would make every first visit slower to serve a benefit
// that only matters on the second. It never rejects — see its own file.
//
// Gated on the same flag as the download surface. Unflagged, this installed a worker and precached
// the whole build for EVERY visitor — including every embedded player on somebody else's site — to
// support a feature none of them could reach, and permanently changed how a deploy reaches a
// returning tab. A worker is not a free thing to leave switched on.
if (isOfflineDownloadsEnabled()) {
    void registerOfflineWorker();
}
